import type { Confidence, VoltageInfo, VoltageReading, VoltageSource } from '@ev/shared'

/**
 * Turns a {@link VoltageInfo} into something a badge can render without making
 * a single judgement call of its own.
 *
 * The whole point of this site is that a driver can tell a measured number from
 * a guessed one, so provenance is not a tooltip afterthought - it is the first
 * field of the model, and there is a third state for "nobody knows" that never
 * collapses into a plausible-looking figure.
 */

export type VoltageProvenance = 'declared' | 'derived' | 'unknown'

/**
 * How the figure itself should read. A DC connector whose bounds are known only
 * as a window renders as a window: flattening 150-500 V to "400 V" would state
 * a precision the data does not have.
 */
export type VoltageFigure =
  | { readonly kind: 'single'; readonly volts: number }
  | { readonly kind: 'range'; readonly minV: number; readonly maxV: number }
  | { readonly kind: 'atLeast'; readonly volts: number }
  | { readonly kind: 'atMost'; readonly volts: number }

/** One source's reading, ready to render. The published figure is one of these. */
export interface ReadingDisplay {
  readonly provenance: VoltageProvenance
  /** `null` only when the connector carries no {@link VoltageInfo} at all. */
  readonly source: VoltageSource | null
  readonly confidence: Confidence | null
  /** `null` when the source is known but the number is not. */
  readonly figure: VoltageFigure | null
  readonly amps: number | null
  readonly phases: number | null
}

export interface VoltageDisplay extends ReadingDisplay {
  /**
   * Readings from other sources that contradict the published one.
   *
   * Empty on all but a handful of connectors, and never collapsed into the
   * figure: where two databases disagree the panel shows both and says how
   * firmly each is tied to this socket, because averaging them or hiding the
   * loser would state a certainty nobody has.
   */
  readonly alternatives: readonly ReadingDisplay[]
}

export const UNKNOWN_VOLTAGE: VoltageDisplay = {
  provenance: 'unknown',
  source: null,
  confidence: null,
  figure: null,
  amps: null,
  phases: null,
  alternatives: [],
}

const isFinitePositive = (value: number | undefined): value is number =>
  value !== undefined && Number.isFinite(value) && value > 0

/**
 * Pick the figure that claims the least.
 *
 * A distinct min/max pair is a range even when a nominal voltage sits beside it
 * - the pair is the honest statement and the nominal is a mid-point. One bound
 * on its own becomes an open-ended figure rather than being quietly dropped.
 */
const toFigure = (voltage: VoltageInfo): VoltageFigure | null => {
  const { nominalV, minV, maxV } = voltage
  const hasMin = isFinitePositive(minV)
  const hasMax = isFinitePositive(maxV)

  if (hasMin && hasMax)
    return minV === maxV ? { kind: 'single', volts: minV } : { kind: 'range', minV, maxV }
  if (isFinitePositive(nominalV)) return { kind: 'single', volts: nominalV }
  if (hasMin) return { kind: 'atLeast', volts: minV }
  if (hasMax) return { kind: 'atMost', volts: maxV }
  return null
}

/**
 * Declared or derived, which is the only distinction the badge draws.
 *
 * Both declaring sources count as declared, and a `ge-declared` figure does so
 * whichever tier matched it. The badge is answering "did somebody publish this
 * number, or did we work it out?", and an address-matched GoingElectric reading
 * is still a number somebody published - what is less certain is that it belongs
 * to *this* connector, which is what `confidence` says and the hint explains.
 * Folding that uncertainty into the badge would tell the reader we reasoned our
 * way to a voltage nobody ever measured, which is a different claim.
 */
const provenanceOf = (source: VoltageSource): VoltageProvenance =>
  source === 'ocm-declared' || source === 'ge-declared' ? 'declared' : 'derived'

/** One reading, on its own terms. Shared by the published figure and each rival. */
const toReadingDisplay = (reading: VoltageReading): ReadingDisplay => {
  const figure = toFigure(reading)

  return {
    // A source with no number is still not a number: it renders as Unknown, and
    // keeps its provenance so the panel can say which source came up empty.
    provenance: figure === null ? 'unknown' : provenanceOf(reading.source),
    source: reading.source,
    confidence: reading.confidence,
    figure,
    amps: isFinitePositive(reading.amps) ? reading.amps : null,
    phases: isFinitePositive(reading.phases) ? reading.phases : null,
  }
}

/**
 * Map a connector's voltage onto the display model.
 *
 * `undefined` in, {@link UNKNOWN_VOLTAGE} out - the pipeline omits the field
 * entirely when nothing could be established, and that is a real answer.
 *
 * An alternative with no figure at all is dropped rather than rendered: "another
 * source disagrees, and here is no number" is noise, not a disagreement.
 */
export const toVoltageDisplay = (voltage: VoltageInfo | undefined): VoltageDisplay => {
  if (voltage === undefined) return UNKNOWN_VOLTAGE

  return {
    ...toReadingDisplay(voltage),
    alternatives: (voltage.alternatives ?? [])
      .map(toReadingDisplay)
      .filter((reading) => reading.figure !== null),
  }
}

/** `400`, `7.4` - trailing zeroes dropped, never rounded to a lie. */
export const formatNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)
